import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg, rememberPhone } from "@/lib/api";
import { ReasonStep } from "./ReasonStep";
import { DateTimeStep } from "./DateTimeStep";
import { DetailsStep } from "./DetailsStep";
import { SandboxPayDialog } from "./SandboxPayDialog";
import { openRazorpay } from "./razorpay";

const STEPS = ["Visit reason", "Date & time", "Details & pay"];
const EMPTY = { patient_name: "", patient_phone: "", patient_email: "", notes: "" };

const Stepper = ({ step, setStep }) => (
  <ol className="flex items-center gap-2 mb-6" data-testid="booking-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className="flex-1">
        <button type="button" disabled={i + 1 > step} onClick={() => setStep(i + 1)} data-testid={`booking-step-indicator-${i + 1}`}
          className="w-full text-left disabled:cursor-default">
          <span className={`block h-1.5 rounded-full transition-colors ${i + 1 <= step ? "bg-teal" : "bg-slate-200"}`} />
          <span className={`mt-2 block text-[11px] sm:text-xs font-medium ${i + 1 === step ? "text-teal" : "text-ink-muted"}`}>{i + 1}. {s}</span>
        </button>
      </li>
    ))}
  </ol>
);

export const BookingWidget = ({ config, preselect }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [reasonId, setReasonId] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [paying, setPaying] = useState(false);
  const [sandbox, setSandbox] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (preselect && config?.visit_reasons.some((r) => r.id === preselect.id)) {
      setReasonId(preselect.id);
      setStep(2);
    }
  }, [preselect, config]);

  const reason = config?.visit_reasons.find((r) => r.id === reasonId);
  const fee = reason?.fee ?? config?.consultation_fee ?? 500;

  const slotLost = (msg) => {
    toast.error(msg);
    setSlot(null);
    setStep(2);
    setRefreshKey((k) => k + 1);
  };
  const release = (b) => api.post(`/bookings/${b.id}/release`, { booking_ref: b.booking_ref }).catch(() => {}).finally(() => setRefreshKey((k) => k + 1));
  const done = (b) => {
    rememberPhone(b.id, form.patient_phone);
    navigate(`/confirmation/${b.id}`);
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/bookings/initiate", {
        ...form, visit_reason_id: reasonId, appointment_date: date, slot_time: slot.time,
      });
      if (data.payment_mode === "live") {
        await openRazorpay(data, {
          onSuccess: async (res) => {
            try {
              await api.post(`/bookings/${data.id}/verify`, res);
              done(data);
            } catch (e) { toast.error(errMsg(e)); }
          },
          onDismiss: () => { release(data); toast("Payment cancelled. Your slot has been released."); },
          onFail: (m) => { release(data); toast.error(m); },
        });
      } else {
        setSandbox(data);
      }
    } catch (e) {
      if (e?.response?.status === 409) slotLost(errMsg(e));
      else toast.error(errMsg(e));
    } finally {
      setPaying(false);
    }
  };

  return (
    <div id="book" className="rounded-3xl bg-white border border-mint-border/70 shadow-[0_30px_60px_-30px_rgba(15,76,92,0.45)] p-5 sm:p-7" data-testid="booking-widget">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-semibold text-ink">Book your consultation</h2>
          <p className="text-sm text-ink-muted mt-1">30 minutes with Dr. Vilayath Mahamadi · ₹{fee} flat fee</p>
        </div>
        {config?.payment_mode === "sandbox" && (
          <span className="shrink-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold" data-testid="sandbox-mode-badge">Demo payments</span>
        )}
      </div>
      <Stepper step={step} setStep={setStep} />
      {!config ? (
        <div className="py-16 grid place-items-center text-ink-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : step === 1 ? (
        <ReasonStep reasons={config.visit_reasons} value={reasonId}
          onSelect={(id) => { setReasonId(id); setStep(2); }} />
      ) : step === 2 ? (
        <DateTimeStep date={date} setDate={setDate} slot={slot} setSlot={setSlot} refreshKey={refreshKey}
          reasonTitle={reason?.title} onBack={() => setStep(1)} onNext={() => setStep(3)} />
      ) : (
        <DetailsStep form={form} setForm={setForm} reason={reason} date={date} slot={slot} fee={fee}
          paying={paying} onPay={pay} onEdit={setStep} />
      )}
      <SandboxPayDialog booking={sandbox} onClose={() => setSandbox(null)} onRelease={release}
        onPaid={done} onExpired={slotLost} />
    </div>
  );
};
